import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { errorText } from '../components/custom_creative_session/DevelopmentStage';
import { visibleForSession } from './sessionScope';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The CTA step's behaviour — ONE implementation, two screens.
 *
 * WAVE 3, SCREEN 5. **This step is the best-built of the seven**, and the sweep
 * says so rather than inventing faults to match the others: it already had
 * three load states, already fetched before scoping instead of skipping the
 * fetch on a new session, and already scoped on `session_id`. Those fixes
 * landed here first, in item 6, and they held.
 *
 * WHAT THE SWEEP DID FIND — two, both in the custom-CTA path:
 *
 *   1. `addCustomCta` used `alert()`.
 *   2. Worse: it checked `response.data?.status !== 'error'` to decide whether
 *      to refetch, and then **closed the form either way, saying nothing**. A
 *      CTA the operator typed and that the server refused vanished with the
 *      form, indistinguishable from one that saved. Rule 10.
 *
 * WHAT THE SWEEP CHECKED AND WOULD NOT CALL A DEFECT. **No script in production
 * carries `cta_element_id` — 0 of 288** — and 33 CTA candidates have been
 * generated since this step shipped. That looks like a broken select, and it is
 * not: the write path is sound (route field matches the client, the guards are
 * right, candidates carry both `script_id` and `element_number`), and the 24
 * active sessions sitting on step 4 all PREDATE the step by 30-90 days. The
 * honest reading is that nobody has driven a selection through yet. **The single
 * check that would settle it: select a CTA and see `cta_element_id` appear.**
 */
export function useCtas({ offer, sessionId, isNewSession, sessionStartTime,
                          angleId, onComplete, refreshKey = 0 }) {
  const [scripts, setScripts] = useState([]);
  const [ctas, setCtas] = useState([]);
  const [hidden, setHidden] = useState({ hiddenOtherSession: 0, hiddenUnstamped: 0, predateNote: 0 });

  const [loadState, setLoadState] = useState('idle');
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [note, setNote] = useState(null);

  const [busy, setBusy] = useState(null);   // script_id or element_id

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      const params = { offer_id: offer.offer_id, element_type: 'CTA', limit: 500 };
      const scriptParams = angleId ? { angle_id: angleId } : undefined;
      const [scriptRes, ctaRes] = await Promise.all([
        axios.get(`${API}/offers/${offer.offer_id}/scripts`,
                  scriptParams ? { params: scriptParams } : undefined),
        axios.get(`${API}/copy-elements`, { params }),
      ]);
      const view = visibleForSession(scriptRes.data.scripts || [], sessionId,
                                     { isNewSession, sessionStartTime });
      setScripts(view.rows);
      setHidden({
        hiddenOtherSession: view.hiddenOtherSession,
        hiddenUnstamped: view.hiddenUnstamped,
        predateNote: view.predateNote,
      });
      setCtas(ctaRes.data.elements || ctaRes.data.copy_elements || []);
      setLoadState('ok');
      setLoadError(null);
    } catch (error) {
      setLoadState('failed');
      setLoadError(errorText(error));
    }
  }, [offer.offer_id, sessionId, isNewSession, sessionStartTime, angleId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  /** Every CTA written for one script, newest last. */
  const ctasFor = (scriptId) => ctas.filter((c) => c.script_id === scriptId);

  const generateCtas = async (scriptId, feedback) => {
    setBusy(scriptId);
    setActionError(null);
    const before = ctasFor(scriptId).length;
    try {
      const res = await axios.post(`${API}/scripts/${scriptId}/ctas/generate`,
                                   { count: 6, feedback: feedback || undefined });
      await load();
      const added = (res.data?.ctas || res.data?.elements || []).length;
      setNote(added
        ? `Added ${added} CTA${added > 1 ? 's' : ''} for this script.`
        : 'The model returned no CTAs. Nothing was added.');
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    } finally {
      setBusy(null);
      void before;
    }
  };

  const selectCta = async (scriptId, elementId) => {
    setBusy(elementId);
    setActionError(null);
    try {
      const res = await axios.post(`${API}/scripts/${scriptId}/cta/select`,
                                   { element_id: elementId });
      setScripts((prev) => prev.map((s) => (s.script_id === scriptId
        ? { ...s, cta_element_id: elementId,
            cta_element_number: res.data?.cta_element_number }
        : s)));
      await load();
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    } finally {
      setBusy(null);
    }
  };

  /**
   * Add a CTA the operator wrote.
   *
   * THE DEFECT THIS FIXES: the old handler refetched only when the response was
   * not an error, then closed the form regardless and said nothing. A refused
   * CTA disappeared exactly like a saved one. A 200 carrying `status: "error"`
   * is a refusal, and it is reported as one.
   */
  const addCustomCta = async (scriptId, text) => {
    const content = (text || '').trim();
    if (!content) return false;
    setBusy(scriptId);
    setActionError(null);
    try {
      const res = await axios.post(`${API}/scripts/${scriptId}/cta/custom`,
                                   { content });
      if (res.data?.status === 'error') {
        setActionError(
          `That CTA was not saved: ${res.data.error || 'the server refused it'}. `
          + `Your text is still in the box.`);
        return false;
      }
      await load();
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    } finally {
      setBusy(null);
    }
  };

  return {
    scripts, ctas, ctasFor,
    ...hidden,
    loadState, loadError, retryLoad: load,
    busy, generateCtas, selectCta, addCustomCta,
    actionError, setActionError, note,
    proceed: onComplete,
  };
}
